import React, { useState, useMemo } from "react";
import Modal from "../../../../../components/Atomsv2/Modal/Modal";
import Table from "../../../../../components/moleculesv2/Table/Table";
import Column from "../../../../../components/moleculesv2/Table/Column";
import OptionsGroup from "../../../../../components/moleculesv2/OptionsGroup/OptionsGroup";
import './GaficaYBarrasDeZona.scss'
import {
    Area,
    AreaChart,
    CartesianGrid,
    Tooltip,
    XAxis,
    YAxis,
    ComposedChart,
    LabelList,
    Bar,
    Line,
    ResponsiveContainer,
} from "recharts";

// ─── Opciones de métricas / período ──────────────────────────────────────────
const METRICAS_OPTIONS = [
    { label: "Incidencias",    value: "INCIDENTES_OPERATIVOS"   },
    { label: "Servicios",      value: "SERVICIOS_EJECUTADOS_TOTAL" },
    { label: "Eficiencia",     value: "EFICIENCIA_OPERATIVA_PCT" },
    { label: "Facturación",    value: "FACTURACION_REAL"         },
    { label: "Todo",           value: "TODO"                     },
];

const PERIODOS_OPTIONS = [
    { label: "4 semanas",    value: "4"   },
    { label: "Mostrar todo", value: "all" },
];

// ─── Colores ──────────────────────────────────────────────────────────────────
const METRICA_COLOR = {
    INCIDENTES_OPERATIVOS:    "#C55A11",
    SERVICIOS_EJECUTADOS_TOTAL: "#1B3A6B",
    EFICIENCIA_OPERATIVA_PCT: "#548235",
    FACTURACION_REAL:         "#8884d8",
};

const AXIS_STYLE = { fontSize: 11, fill: "#64748b" };

const calcDomain = (valores) => {
    const validos = valores.filter(v => v != null && !isNaN(v));
    if (!validos.length) return [0, "auto"];
    const min = Math.min(...validos);
    const max = Math.max(...validos);
    const pad = Math.max(0.5, (max - min) * 0.15);
    return [parseFloat((min - pad).toFixed(2)), parseFloat((max + pad).toFixed(2))];
};

const formatMoneda = (v) =>
    v != null
        ? v.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
        : "—";

// ─── Componente ───────────────────────────────────────────────────────────────
const ModalClienteDetalle = ({ data, setData }) => {
    const [metrica, setMetrica] = useState("TODO");
    const [periodo, setPeriodo] = useState("4");

    const dataVista = useMemo(() => {
        const base = data ?? [];
        if (periodo === "4") {
            // Clave compuesta AÑO*100+SEMANA para comparar correctamente entre años
            const claves = [...new Set(base.map(d => d.AÑO * 100 + d.SEMANA))]
                .sort((a, b) => a - b)
                .slice(-4);
            return base.filter(d => claves.includes(d.AÑO * 100 + d.SEMANA));
        }
        return base;
    }, [data, periodo]);

    // Datos ordenados para área/línea
    const chartData = useMemo(() =>
        [...dataVista]
            .sort((a, b) => a.SEMANA - b.SEMANA)
            .map(row => ({
                semana:      `S${row.SEMANA}`,
                valor:        row[metrica],
                fechaInicio:  row.FECHA_INICIO_SEMANA ?? "",
                fechaFin:     row.FECHA_FIN_SEMANA    ?? "",
            })),
        [dataVista, metrica]
    );

    // Datos para ComposedChart (TODO)
    const composedData = useMemo(() =>
        [...dataVista]
            .sort((a, b) => a.SEMANA - b.SEMANA)
            .map(row => ({
                semana:       `S${row.SEMANA}`,
                servicios:    row.SERVICIOS_EJECUTADOS_TOTAL,
                incidencias:  row.INCIDENTES_OPERATIVOS,
                eficiencia:   row.EFICIENCIA_OPERATIVA_PCT,
            })),
        [dataVista]
    );

    const yDomain      = useMemo(() => calcDomain(chartData.map(d => d.valor)),         [chartData]);
    const yDomainEfic  = useMemo(() => calcDomain(composedData.map(d => d.eficiencia)), [composedData]);

    const labelMetrica = METRICAS_OPTIONS.find(m => m.value === metrica)?.label ?? "";

    const esFact = metrica === "FACTURACION_REAL";

    return (
        <Modal
            onClose={() => setData([])}
            open={!!data?.length}
            title={`Detalle de ${data?.[0]?.CLIENTE?.trim() ?? ""}`}
            className="ModalDataIndividual"
        >
            {/* ─── Controles ─── */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
                <OptionsGroup
                    style={{ width: "fit-content" }}
                    label="Métrica"
                    options={METRICAS_OPTIONS}
                    activeValue={metrica}
                    onSelect={setMetrica}
                />
                <OptionsGroup
                    style={{ width: "fit-content" }}
                    label="Período"
                    options={PERIODOS_OPTIONS}
                    activeValue={periodo}
                    onSelect={setPeriodo}
                />
            </div>

            {/* ─── Gráfica ─── */}
            <ResponsiveContainer width="100%" height={320}>
                {metrica === "TODO" ? (
                    <ComposedChart data={composedData} margin={{ top: 24, right: 50, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="semana" tick={AXIS_STYLE} />
                        <YAxis yAxisId="left"  tick={AXIS_STYLE} axisLine={false} tickLine={false} />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            domain={yDomainEfic}
                            tick={AXIS_STYLE}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={v => `${v}%`}
                        />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }} />
                        <Area
                            yAxisId="right"
                            type="linear"
                            dataKey="eficiencia"
                            name="Eficiencia %"
                            fill="#548235"
                            fillOpacity={0.15}
                            stroke="#548235"
                            strokeWidth={1.5}
                            dot={false}
                        >
                            <LabelList dataKey="eficiencia" position="top" style={{ fontSize: 10, fill: "#548235", fontWeight: 600 }} />
                        </Area>
                        <Bar
                            yAxisId="left"
                            dataKey="servicios"
                            name="Servicios"
                            fill="#1B3A6B"
                            fillOpacity={0.85}
                            radius={[3, 3, 0, 0]}
                            barSize={18}
                        >
                            <LabelList dataKey="servicios" position="top" style={{ fontSize: 10, fill: "#1B3A6B", fontWeight: 600 }} />
                        </Bar>
                        <Line
                            yAxisId="left"
                            type="linear"
                            dataKey="incidencias"
                            name="Incidencias"
                            stroke="#C55A11"
                            strokeWidth={2}
                            dot={{ r: 5, fill: "#C55A11", strokeWidth: 0 }}
                            activeDot={{ r: 7, strokeWidth: 0 }}
                        >
                            <LabelList dataKey="incidencias" position="top" style={{ fontSize: 10, fill: "#C55A11", fontWeight: 600 }} />
                        </Line>
                    </ComposedChart>
                ) : (
                    <AreaChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="4 4" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="semana" tick={AXIS_STYLE} />
                        <YAxis
                            domain={yDomain}
                            tick={AXIS_STYLE}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={esFact ? v => `$${(v / 1000).toFixed(0)}k` : undefined}
                        />
                        <Tooltip
                            contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e2e8f0" }}
                            formatter={(val) => [esFact ? formatMoneda(val) : val, labelMetrica]}
                        />
                        <defs>
                            <linearGradient id="gradModalDetalle" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor={METRICA_COLOR[metrica]} stopOpacity={0.25} />
                                <stop offset="95%" stopColor={METRICA_COLOR[metrica]} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <Area
                            type="linear"
                            dataKey="valor"
                            name={labelMetrica}
                            stroke={METRICA_COLOR[metrica]}
                            strokeWidth={2}
                            fill="url(#gradModalDetalle)"
                            dot={{ r: 5, fill: METRICA_COLOR[metrica], strokeWidth: 0 }}
                            activeDot={{ r: 7, strokeWidth: 0 }}
                        >
                            <LabelList
                                dataKey="valor"
                                position="top"
                                style={{ fontSize: 11, fill: METRICA_COLOR[metrica], fontWeight: 600 }}
                                formatter={esFact ? (v) => `$${(v / 1000).toFixed(0)}k` : undefined}
                            />
                        </Area>
                    </AreaChart>
                )}
            </ResponsiveContainer>

            {/* ─── Tabla ─── */}
            <Table data={dataVista} stripedRows height={280}>
                <Column header="Semana"    value="SEMANA"                    align="center" width="80px"  sortable />
                <Column header="Inicio"    value="FECHA_INICIO_SEMANA"       align="center" width="110px" />
                <Column header="Final"     value="FECHA_FIN_SEMANA"          align="center" width="110px" />
                <Column header="Servicios" value="SERVICIOS_EJECUTADOS_TOTAL" align="center" width="100px" sortable />
                <Column header="Incidentes" value="INCIDENTES_OPERATIVOS"    align="center" width="100px" sortable />
                <Column
                    header="Eficiencia"
                    value="EFICIENCIA_OPERATIVA_PCT"
                    align="center"
                    width="100px"
                    sortable
                    body={row => row.EFICIENCIA_OPERATIVA_PCT != null ? `${row.EFICIENCIA_OPERATIVA_PCT}%` : "—"}
                />
                <Column
                    header="Facturación Real"
                    value="FACTURACION_REAL"
                    align="right"
                    width="140px"
                    sortable
                    body={row => formatMoneda(row.FACTURACION_REAL)}
                />
            </Table>
        </Modal>
    );
};

export default ModalClienteDetalle;
